import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { OctagonAlert, ShieldCheck } from 'lucide-react';

type LogItem = {
  id: string;
  action: string;
  details: any;
  created_at: string;
};

export const ProtectionHistory: React.FC = () => {
  const fetchOperatorLogs = useSupabaseStore(s => s.fetchOperatorLogs);
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const data = await fetchOperatorLogs(200);
    const filtered = (data || []).filter(
      (l) => l.action === 'ai_access_stop' || l.action === 'ai_access_resume'
    ) as LogItem[];
    setLogs(filtered);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="h-full w-full flex flex-col">
      <Card className="border-0 rounded-none">
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-lg">Protection History</CardTitle>
          <Button size="sm" variant="secondary" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </CardHeader>
      </Card>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {logs.length === 0 && !loading && (
            <div className="text-sm text-muted-foreground">No AI access events yet.</div>
          )}
          {logs.map((item) => {
            const isStop = item.action === 'ai_access_stop';
            return (
              <div
                key={item.id}
                className="flex items-start gap-3 rounded-md border p-3 bg-background"
              >
                <div
                  className={`rounded-md p-2 ${isStop ? 'bg-destructive/10 text-destructive' : 'bg-emerald-100/40 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'}`}
                >
                  {isStop ? <OctagonAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {isStop ? 'AI access stopped' : 'AI access resumed'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(item.created_at).toLocaleString()}
                  </div>
                  {item.details && (
                    <div className="mt-1 text-xs text-muted-foreground truncate">
                      {item.details?.reason ? `Reason: ${item.details.reason}` : ''}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ProtectionHistory;